// StepFaceVerify — Step 1.2 face active liveness + 1:N descriptor match.
// Spec: docs/specs/location-tasks.md §8.4.2.
//
// Flow:
//   1. GET  /v1/patrols/submissions/:id/verify/face/challenge
//      → backend picks a random action ∈ {blink, smile, turn_left, turn_right}.
//   2. Lazy-load face-api.js + models from jsDelivr (same CDN as enroll).
//   3. Open user-facing camera, run a tinyFaceDetector + landmarks +
//      descriptor loop on requestAnimationFrame.
//   4. Detect the action via blendshape/pose heuristics (we don't have
//      MediaPipe's blendshape coefficients here, so we approximate from
//      the 68-pt landmarks the same way EnrollFacePage does for pose,
//      plus an eye-aspect-ratio for blinks and a smile heuristic).
//   5. When the action fires, capture the current frame + descriptor.
//   6. POST /verify/face/finish { challenge_id, action, descriptor,
//      photo_b64, completed_at } → backend compares descriptor to
//      enrolled set, uploads photo to R2.

import React, { useEffect, useRef, useState } from "react";
import { ScanFace, Loader2, AlertTriangle } from "lucide-react";
import { useLiff } from "../../../liff/index.js";
import { apiFetch } from "../../../lib/api-client.js";
import type { WizardData } from "./types.js";

const FACEAPI_LIB_URL = "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js";
const FACEAPI_MODELS_URL = "https://justadudewhohacks.github.io/face-api.js/models";

interface FaceApi {
  nets: {
    tinyFaceDetector: { loadFromUri(u: string): Promise<void> };
    faceLandmark68Net: { loadFromUri(u: string): Promise<void> };
    faceRecognitionNet: { loadFromUri(u: string): Promise<void> };
  };
  TinyFaceDetectorOptions: new (opts?: { inputSize?: number; scoreThreshold?: number }) => unknown;
  detectSingleFace(input: HTMLVideoElement | HTMLCanvasElement, opts?: unknown): {
    withFaceLandmarks(): { withFaceDescriptor(): Promise<{
      detection: { box: { x: number; y: number; width: number; height: number } };
      landmarks: { positions: { x: number; y: number }[] };
      descriptor: Float32Array;
    } | undefined> };
  };
}

declare global {
  interface Window { faceapi?: FaceApi }
}

type Action = "blink" | "smile" | "turn_left" | "turn_right";
type Phase = "idle" | "challenging" | "loading_ml" | "camera_starting" | "ready" | "submitting" | "done" | "error";

interface Props {
  data: WizardData;
  onComplete: () => void;
}

export default function StepFaceVerify({ data, onComplete }: Props): React.ReactElement {
  const { session } = useLiff();
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<Action | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const [result, setResult] = useState<{ matchScore: number; review: boolean } | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const eyeOpenSeenRef = useRef(false);

  // Tick clock so the countdown re-renders.
  useEffect(() => {
    if (phase !== "ready") return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [phase]);

  useEffect(() => () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const start = async (): Promise<void> => {
    if (!session?.session_token) return;
    setError(null);
    setPhase("challenging");
    try {
      const r = await apiFetch(
        `/v1/patrols/submissions/${encodeURIComponent(data.submission.id)}/verify/face/challenge`,
        {},
        { jwt: session.session_token },
      );
      const body = (await r.json().catch(() => ({}))) as { challenge_id?: string; action?: Action; expires_in?: number; error?: string };
      if (!r.ok || !body.challenge_id || !body.action) {
        setError(body.error ?? `HTTP ${r.status}`);
        setPhase("error");
        return;
      }
      setAction(body.action);
      setChallengeId(body.challenge_id);
      setExpiresAt(Date.now() + (body.expires_in ?? 60) * 1000);
      eyeOpenSeenRef.current = false;
      setPhase("loading_ml");
    } catch (e) {
      setError((e as Error).message);
      setPhase("error");
    }
  };

  // Load face-api.js + models.
  useEffect(() => {
    if (phase !== "loading_ml") return;
    let cancelled = false;
    void (async () => {
      try {
        if (!window.faceapi) await loadScript(FACEAPI_LIB_URL);
        if (!window.faceapi) throw new Error("face-api.js failed to load");
        await Promise.all([
          window.faceapi.nets.tinyFaceDetector.loadFromUri(FACEAPI_MODELS_URL),
          window.faceapi.nets.faceLandmark68Net.loadFromUri(FACEAPI_MODELS_URL),
          window.faceapi.nets.faceRecognitionNet.loadFromUri(FACEAPI_MODELS_URL),
        ]);
        if (!cancelled) setPhase("camera_starting");
      } catch (e) {
        if (!cancelled) {
          setError("人臉模型載入失敗：" + (e as Error).message);
          setPhase("error");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [phase]);

  // Camera.
  useEffect(() => {
    if (phase !== "camera_starting") return;
    let cancelled = false;
    void (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setPhase("ready");
      } catch (e) {
        if (cancelled) return;
        setError(((e as Error).name === "NotAllowedError")
          ? "需要相機存取權限。請允許後再試。"
          : "無法開啟相機：" + (e as Error).message);
        setPhase("error");
      }
    })();
    return () => { cancelled = true; };
  }, [phase]);

  // Detection loop.
  useEffect(() => {
    if (phase !== "ready" || !action) return;
    let stopped = false;
    let raf = 0;

    const tick = async (): Promise<void> => {
      if (stopped || !videoRef.current || !window.faceapi) return;
      if (videoRef.current.readyState < 2) {
        raf = requestAnimationFrame(tick); return;
      }
      if (expiresAt && Date.now() > expiresAt) {
        setError("挑戰已逾時，請重新開始。");
        setPhase("error");
        return;
      }
      try {
        const det = await window.faceapi
          .detectSingleFace(videoRef.current, new window.faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 }))
          .withFaceLandmarks()
          .withFaceDescriptor();
        if (!stopped && det) {
          const detected = matchAction(det.landmarks.positions, action, eyeOpenSeenRef);
          if (detected) {
            // Capture frame + descriptor
            const photo = grabFrame(videoRef.current, det.detection.box);
            stopped = true;
            await submit(det.descriptor, photo);
            return;
          }
        }
      } catch { /* transient detect noise */ }
      if (!stopped) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { stopped = true; cancelAnimationFrame(raf); };
  }, [phase, action, expiresAt]);

  const submit = async (descriptor: Float32Array, photoB64: string): Promise<void> => {
    if (!session?.session_token || !challengeId || !action) return;
    setPhase("submitting");
    streamRef.current?.getTracks().forEach((t) => t.stop());
    try {
      const r = await apiFetch(
        `/v1/patrols/submissions/${encodeURIComponent(data.submission.id)}/verify/face/finish`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            challenge_id: challengeId,
            action,
            completed_at: Date.now(),
            descriptor: Array.from(descriptor),
            photo_b64: photoB64.replace(/^data:image\/[a-z]+;base64,/, ""),
          }),
        },
        { jwt: session.session_token },
      );
      const body = (await r.json().catch(() => ({}))) as { ok?: boolean; match_score?: number; requires_manual_review?: boolean; error?: string };
      if (!r.ok || !body.ok) {
        setError(body.error === "no_face_enrolled"
          ? "您尚未註冊人臉樣本，請聯絡管理員產生註冊連結。"
          : `驗證失敗：${body.error ?? r.status}`);
        setPhase("error");
        return;
      }
      setResult({ matchScore: body.match_score ?? 0, review: !!body.requires_manual_review });
      setPhase("done");
      onComplete();
    } catch (e) {
      setError("送出失敗：" + (e as Error).message);
      setPhase("error");
    }
  };

  if (phase === "idle") {
    return (
      <button onClick={start} className="block w-full h-12 rounded-xl bg-accent text-text-inverse font-semibold text-sm">
        <span className="inline-flex items-center justify-center gap-1"><ScanFace className="h-4 w-4" /> 開始人臉挑戰</span>
      </button>
    );
  }

  if (phase === "challenging" || phase === "loading_ml" || phase === "camera_starting" || phase === "submitting") {
    const label =
      phase === "challenging" ? "取得挑戰中…" :
      phase === "loading_ml" ? "載入模型中（首次約 5–15 秒）…" :
      phase === "camera_starting" ? "啟動相機中…" : "送出比對中…";
    return (
      <div className="flex items-center gap-2 text-sm text-text-secondary py-2">
        <Loader2 className="h-4 w-4 animate-spin" />{label}
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="space-y-2">
        <p className="text-xs text-danger inline-flex items-start gap-1">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5" />{error ?? "未知錯誤"}
        </p>
        <button onClick={start} className="block w-full h-11 rounded-xl bg-bg-raised text-text-primary text-sm font-medium">
          重試
        </button>
      </div>
    );
  }

  if (phase === "done" && result) {
    return (
      <p className="text-xs text-success">
        比對通過（{(result.matchScore * 100).toFixed(0)}/100）{result.review ? " · 人工審核中" : ""}
      </p>
    );
  }

  // ready — camera live
  const remain = expiresAt ? Math.max(0, Math.round((expiresAt - now) / 1000)) : 0;
  return (
    <div className="space-y-2">
      <div className="relative bg-black rounded-xl overflow-hidden aspect-[4/3]">
        <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" style={{ transform: "scaleX(-1)" }} playsInline muted />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="border-4 border-accent/60 rounded-full w-3/4 h-3/4" />
        </div>
        <div className="absolute top-2 left-2 right-2 flex items-center justify-between text-[10px] uppercase tracking-wider text-white">
          <span className="bg-black/60 rounded px-2 py-0.5">挑戰 #{challengeId?.slice(-6)}</span>
          <span className="bg-black/60 rounded px-2 py-0.5">剩餘 {remain}s</span>
        </div>
      </div>
      <p className="text-center text-base font-semibold">{ACTION_PROMPT[action ?? "blink"]}</p>
      <p className="text-center text-2xs text-text-muted">完成動作後系統會自動拍攝並比對。</p>
    </div>
  );
}

const ACTION_PROMPT: Record<Action, string> = {
  blink:      "請眨眼一次",
  smile:      "請微笑",
  turn_left:  "請慢慢向左轉頭",
  turn_right: "請慢慢向右轉頭",
};

// ── helpers ──────────────────────────────────────────────────────────

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      if (window.faceapi) return resolve();
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("script load failed")), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = src; s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("script load failed"));
    document.head.appendChild(s);
  });
}

/** Detect whether the requested action has occurred this frame.
 *  Heuristics derived from face-api.js's 68-pt landmark layout:
 *    - blink: eye aspect ratio (EAR) drops below 0.2 after we saw the
 *      eyes open above 0.27. Tracks an "open seen" flag in a ref.
 *    - smile: mouth-aspect-ratio (width / height) > 3.4 with corners up.
 *    - turn_left / turn_right: yaw < -22 / > 22 (mirrored: video is
 *      scale-x(-1) so user's left feels intuitive).
 */
function matchAction(landmarks: { x: number; y: number }[], action: Action, eyeOpenSeenRef: { current: boolean }): boolean {
  if (landmarks.length < 68) return false;
  if (action === "blink") {
    const earL = eyeAspectRatio(landmarks.slice(36, 42));
    const earR = eyeAspectRatio(landmarks.slice(42, 48));
    const ear = (earL + earR) / 2;
    if (ear > 0.27) eyeOpenSeenRef.current = true;
    if (eyeOpenSeenRef.current && ear < 0.2) return true;
    return false;
  }
  if (action === "smile") {
    // Was > 1 (= mouth-wh > ~3.4 baseline, exaggerated grin). Relaxed
    // to 0 so a normal smile (wh ≈ 2.7 with mild corner-raise) passes;
    // the cornersUp term still rewards real smiles over neutral mouth
    // open.
    return smileScore(landmarks) > 0;
  }
  // pose
  const { yaw } = estimatePose(landmarks);
  // 22° required a chin-over-shoulder turn; 18° is comfortable on a
  // phone held arm's-length without losing the camera frame.
  if (action === "turn_left") return yaw < -18;
  return yaw > 18;
}

function eyeAspectRatio(eye: { x: number; y: number }[]): number {
  // 6 points: P1..P6 around the eye. EAR = (||P2-P6|| + ||P3-P5||) / (2 * ||P1-P4||)
  const d = (a: { x: number; y: number }, b: { x: number; y: number }): number => Math.hypot(a.x - b.x, a.y - b.y);
  const horiz = d(eye[0], eye[3]);
  if (horiz === 0) return 0;
  return (d(eye[1], eye[5]) + d(eye[2], eye[4])) / (2 * horiz);
}

function smileScore(landmarks: { x: number; y: number }[]): number {
  // mouth 48..67. corners 48 + 54, top lip mid 51, bottom 57.
  const left = landmarks[48], right = landmarks[54];
  const top = landmarks[51], bot = landmarks[57];
  const w = Math.hypot(left.x - right.x, left.y - right.y);
  const h = Math.hypot(top.x - bot.x, top.y - bot.y);
  const wh = h > 0 ? w / h : 0;
  // raised corners: corners y above midpoint of top+bot
  const midY = (top.y + bot.y) / 2;
  const cornersUp = ((midY - left.y) + (midY - right.y)) / Math.max(1, w);
  return (wh - 3) + cornersUp * 5;
}

function estimatePose(landmarks: { x: number; y: number }[]): { yaw: number; pitch: number } {
  if (landmarks.length < 68) return { yaw: 0, pitch: 0 };
  const noseTip = landmarks[30];
  const leftEye = avg(landmarks.slice(36, 42));
  const rightEye = avg(landmarks.slice(42, 48));
  const eyeMidX = (leftEye.x + rightEye.x) / 2;
  const yawNorm = (noseTip.x - eyeMidX) / Math.max(1, Math.abs(rightEye.x - leftEye.x));
  return { yaw: yawNorm * 90, pitch: 0 };
}

function avg(pts: { x: number; y: number }[]): { x: number; y: number } {
  let sx = 0, sy = 0;
  for (const p of pts) { sx += p.x; sy += p.y; }
  return { x: sx / pts.length, y: sy / pts.length };
}

function grabFrame(video: HTMLVideoElement, box: { x: number; y: number; width: number; height: number }): string {
  const size = 320;
  const canvas = document.createElement("canvas");
  canvas.width = size; canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  // Mirror to match video CSS scaleX(-1)
  ctx.translate(size, 0); ctx.scale(-1, 1);
  ctx.drawImage(video, box.x, box.y, box.width, box.height, 0, 0, size, size);
  return canvas.toDataURL("image/jpeg", 0.85);
}
